import React from "react";
import { useParams, useLocation, Link, Navigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, Clock, Phone, Car, ArrowRight, Wine, Beer, ExternalLink, ScrollText } from "lucide-react";
import { SEO } from "../components/SEO";
import { Breadcrumbs, buildBreadcrumbLd } from "../components/Breadcrumbs";
import { BIZ, IMG } from "../lib/constants";

/* Content for 8 long-tail SEO landing pages. Each entry produces a real,
   well-written page with internal links, CTAs to /visit and /spirits, and
   structured data (LocalBusiness + BreadcrumbList + optional FAQPage). */

const CONTENT = {
    "clinton-mo": {
        kind: "location",
        breadcrumb: [{ label: "Locations", to: "/local/clinton-mo" }, { label: "Clinton, MO" }],
        title: "Liquor Store in Clinton, Missouri",
        h1: ["Clinton's flagship", "liquor studio."],
        eyebrow: "Clinton, MO",
        description:
            "Liquor Studio is the largest, most curated liquor store in Clinton, MO — bourbon, wine, craft beer & spirits at 309 S 2nd St. Open seven days. Local pickup via shop.liquor.studio.",
        keywords: "liquor store Clinton MO, Clinton Missouri liquor store, best liquor Clinton MO, wine Clinton MO, bourbon Clinton, craft beer Clinton",
        hero: IMG.storeInteriorWarm,
        intro:
            "We've been Clinton's go-to destination for premium spirits since the day we opened our doors on 2nd Street. Locals, weekenders, and Truman Lake travelers alike walk in for one reason: the selection here is bigger and better-curated than anything within an hour's drive — and the staff actually knows what they're talking about.",
        sections: [
            {
                h: "Why Clinton locals shop with us",
                p: "Henry County deserves more than a beer cooler and three shelves of bourbon. Liquor Studio carries 3,000+ SKUs across bourbon & American whiskey, scotch, tequila, mezcal, rum, gin, vodka, wine (red, white, rosé, sparkling, dessert), craft beer (regional and national), and an ever-growing ready-to-drink section. We special-order anything we don't stock, and we hold allocated bottles for regulars.",
            },
            {
                h: "Easy to find — and easy to park",
                p: "We're right downtown at 309 S 2nd Street, two blocks south of the Henry County Square and one minute off MO-7 and MO-13. There's free street parking out front and a lot behind the store. We're the building with the gold & red sign — you can't miss it.",
            },
            {
                h: "Open seven days, late on weekends",
                p: "Mon–Thu 10 AM – 10 PM, Fri & Sat 10 AM – 11 PM, Sun 10 AM – 10 PM. Holiday hours are posted on /faq and our Google profile. Call ahead for special-occasion holds.",
            },
        ],
    },
    "warsaw-mo": {
        kind: "location",
        breadcrumb: [{ label: "Locations", to: "/local/clinton-mo" }, { label: "Warsaw, MO" }],
        title: "Best Liquor Store Near Warsaw, MO",
        h1: ["Warsaw's nearest", "real liquor shop."],
        eyebrow: "Warsaw, MO",
        description:
            "Liquor Studio in Clinton is just 25 minutes from Warsaw, MO. Better selection, fairer prices, expert staff. The right stop before Truman Lake or Sedalia.",
        keywords: "liquor store Warsaw MO, Warsaw Missouri liquor, near Warsaw MO bourbon, Truman Lake liquor",
        hero: IMG.trumanLakeSunset,
        intro:
            "Warsaw has plenty of charm, but if you're after a serious bourbon selection, allocated picks, or a properly stocked wine wall — you're driving to Clinton. We make the 25-minute trip worth it.",
        sections: [
            {
                h: "From Warsaw, MO — 25 minutes",
                p: "Take MO-7 W out of Warsaw straight to Clinton. We're on 2nd Street, three blocks south of the Square. Plenty of parking. If you're headed onward to Sedalia or back to Truman Lake, we're right on your route.",
            },
            {
                h: "Worth the drive — here's why",
                p: "Local Warsaw stores carry the basics. We carry the basics PLUS allocated bourbon, regional craft beer (Kansas City, St. Louis, Springfield, Ozarks), proper natural & biodynamic wine, and a 100+ deep tequila/mezcal wall. Special orders run through us and arrive in days, not weeks.",
            },
            {
                h: "Lake weekend? Call ahead.",
                p: `Have a marina arrival deadline? Call (660) 383-1751 and we'll have your case waiting by the door. We do this every weekend for Truman Lake regulars — first-name basis with most of them.`,
            },
        ],
    },
    "sedalia-mo": {
        kind: "location",
        breadcrumb: [{ label: "Locations", to: "/local/clinton-mo" }, { label: "Sedalia, MO" }],
        title: "Liquor Store Near Sedalia, MO",
        h1: ["Sedalia, meet your", "new bourbon shop."],
        eyebrow: "Sedalia, MO",
        description:
            "Liquor Studio in Clinton is 35 minutes from Sedalia via US-65. Bigger bourbon shelf, better wine, expert recommendations. The Sedalia detour worth taking.",
        keywords: "liquor store Sedalia MO, Sedalia Missouri liquor, bourbon Sedalia MO, near Sedalia liquor",
        hero: IMG.bourbonCollectionShelf,
        intro:
            "Sedalia regulars tell us the same thing: 'We didn't know a store like this existed within driving distance.' That's our favorite review. Clinton is 35 minutes from Sedalia and you'll find more on our bourbon wall than anywhere between Kansas City and Columbia.",
        sections: [
            {
                h: "From Sedalia — 35 minutes via US-65",
                p: "Take US-65 S to MO-7 W into Clinton. Straight shot. We're at 309 S 2nd St — two blocks south of the Square, on the right.",
            },
            {
                h: "What Sedalia regulars come for",
                p: "Allocated bourbon (Buffalo Trace, Eagle Rare, Weller, Blanton's when we get it). Single-barrel picks. Limited craft beer drops from Boulevard, KC Bier, Boulevard, Logboat, Schlafly. A wine selection that actually has Burgundy and Loire under $40.",
            },
            {
                h: "Special orders, fast",
                p: "Looking for something allocated or hard-to-find? Email us or call. If it's available in Missouri, we can usually have it in-store within 5 business days. No minimum order — one bottle is fine.",
            },
        ],
    },
    "truman-lake": {
        kind: "location",
        breadcrumb: [{ label: "Locations", to: "/local/clinton-mo" }, { label: "Truman Lake" }],
        title: "Liquor Store Near Truman Lake",
        h1: ["Your lake weekend", "starts here."],
        eyebrow: "Truman Lake",
        description:
            "Stock up at Liquor Studio in Clinton before heading to Truman Lake — 10 minutes from most marinas. Curated for the cabin, the boat, and the bonfire.",
        keywords: "Truman Lake liquor store, Truman Lake Missouri liquor, Truman Reservoir liquor, lake weekend bourbon",
        hero: IMG.trumanLakeSunset,
        intro:
            "Truman Lake is one of Missouri's best-kept secrets and we treat it that way. Whether you're heading to Berry Bend, Sterett Creek, Talley Bend, or one of the Cooper Creek marinas — Clinton is your last real stop for a proper liquor selection before you hit the water.",
        sections: [
            {
                h: "10 minutes from most marinas",
                p: "Berry Bend: 12 min. Sterett Creek: 14 min. Talley Bend: 18 min. Bucksaw: 30 min (still closer than driving anywhere else). We're on 2nd Street in Clinton — same exit you'd take for groceries, gas, and bait.",
            },
            {
                h: "Stocked for the lake, not a warehouse",
                p: "Cans (because boats), single-serve wines (because pontoon docking), spiked seltzer, low-ABV options for the long days on the water, and a serious bourbon shelf for the campfire after. We also carry koozies, ice (when we have it), and corkscrews.",
            },
            {
                h: "Call ahead — we'll have it waiting",
                p: `Trying to make it on the water by 11 AM Saturday? Call (660) 383-1751 Friday and we'll have your case bundled, iced, and ready to load. Most Truman Lake regulars do this — saves 20 minutes.`,
            },
        ],
    },
    "bourbon-clinton-mo": {
        kind: "category",
        breadcrumb: [{ label: "Spirits", to: "/spirits" }, { label: "Bourbon — Clinton, MO" }],
        title: "Best Bourbon Selection in Clinton, MO",
        h1: ["The bourbon wall.", "Clinton's deepest."],
        eyebrow: "Bourbon — Clinton, MO",
        description:
            "Hunt allocated bourbon in Clinton, MO at Liquor Studio. Single barrels, store picks, Buffalo Trace family, Wild Turkey & limited releases. The deepest bourbon wall in Henry County.",
        keywords: "bourbon Clinton MO, allocated bourbon Missouri, Buffalo Trace Clinton, single barrel bourbon Missouri, bourbon store Henry County",
        hero: IMG.bourbonCollectionShelf,
        intro:
            "Bourbon is what made us. It's where we obsess — the shelf is curated weekly, the staff knows every release, and our allocation list is by name. If there's a bottle worth chasing, there's a good chance it's on our wall, in our backstock, or being held for a regular.",
        sections: [
            {
                h: "What you'll find on the wall",
                p: "Workhorses: Buffalo Trace, Maker's, Wild Turkey 101, Eagle Rare, Knob Creek, Four Roses Small Batch & Single Barrel. Step-up: Weller Special Reserve, Antique 107, Henry McKenna 10, Russell's Reserve 10, Elijah Craig Barrel Proof, Stagg Jr. Allocated when we get it: BTAC, William Larue Weller, Blanton's Gold/Straight from the Barrel, E.H. Taylor, George T. Stagg.",
                cta: { label: "Browse bourbon", to: "/spirits/category/bourbon" },
            },
            {
                h: "Store picks & single barrels",
                p: "We pick our own barrels. Current rotation often includes Russell's Reserve, Four Roses, Knob Creek, and Wild Turkey single-barrel store picks — all priced fairly, none gouged. Ask in-store for what's currently open as a sample.",
            },
            {
                h: "Allocation list — how it works",
                p: "If you're a regular and you want first crack at the next BTAC drop, ask us. We keep a written list. No deposits, no obligations — just a fair shake at the bottles when they land.",
            },
        ],
    },
    "wine-clinton-mo": {
        kind: "category",
        breadcrumb: [{ label: "Spirits", to: "/spirits" }, { label: "Wine — Clinton, MO" }],
        title: "Wine Selection in Clinton, MO",
        h1: ["Curated wine.", "Real opinions."],
        eyebrow: "Wine — Clinton, MO",
        description:
            "Liquor Studio's wine wall in Clinton, MO is curated by people who actually drink wine. Burgundy, Loire, Piedmont, California, Missouri. Real recommendations.",
        keywords: "wine Clinton MO, wine store Clinton Missouri, natural wine Missouri, Missouri wine selection",
        hero: IMG.wineBottlesElegant,
        intro:
            "Most Henry County wine selections stop at $15 Cabernet and Yellow Tail. Ours doesn't. We carry serious producer-driven wines from the Loire, Burgundy, Piedmont, Mosel, and the California coast — plus a strong Missouri-grown shelf because we should support our neighbors.",
        sections: [
            {
                h: "What we focus on",
                p: "Old World: Loire whites & reds, Burgundy under $50, Beaujolais crus, Northern Rhône, Piedmont (Barolo, Barbaresco, Dolcetto), German Riesling, Spanish Rioja & Ribera. New World: Oregon Pinot, Washington Syrah, California natural & biodynamic. Plus our Missouri shelf for what's actually growing well here.",
                cta: { label: "Browse wine", to: "/spirits/category/wine" },
            },
            {
                h: "Tell us what you're cooking",
                p: "Roasted chicken? Steak? Spicy thai takeout? Walk in and tell us the meal, the budget, and how adventurous you're feeling. We'll have you out the door with the right bottle in three minutes.",
            },
            {
                h: "Wedding & event ordering",
                p: "We handle weddings, rehearsal dinners, anniversaries, and corporate events. Bring us the guest count, the menu, and the budget — we'll quote a case-discount package and deliver if you're local. See /contact to start.",
            },
        ],
    },
    "craft-beer-mid-missouri": {
        kind: "category",
        breadcrumb: [{ label: "Spirits", to: "/spirits" }, { label: "Craft Beer — Mid-Missouri" }],
        title: "Craft Beer in Mid-Missouri",
        h1: ["Cold, fresh,", "and from here."],
        eyebrow: "Craft Beer — Mid-Missouri",
        description:
            "Regional craft beer in Clinton, MO — Boulevard, KC Bier, Logboat, Schlafly, Mother's, 4 Hands, and more. Rotating tap-quality cans, fresh and cold.",
        keywords: "craft beer Clinton MO, craft beer mid Missouri, Boulevard Brewing Clinton, KC Bier, Logboat, regional craft beer Missouri",
        hero: IMG.craftBeerLineup,
        intro:
            "We treat craft beer like wine: fresh, rotated, and only what's actually good. Cooler space is precious and ours goes to regional breweries who do the work — plus a handful of national standards. If a release is hyped and worth it, we chase it.",
        sections: [
            {
                h: "Who we stock heaviest",
                p: "Boulevard (KC), KC Bier Co, Logboat (Columbia), Schlafly & 4 Hands (St. Louis), Mother's (Springfield), Public House (Rolla), plus a Missouri-only seasonal shelf. Out-of-state: Founders, Bell's, Sierra Nevada, Dogfish, Allagash, Modern Times, Pinthouse, Other Half when we can get it.",
                cta: { label: "Browse craft beer", to: "/spirits/category/beer" },
            },
            {
                h: "Fresh dating, always",
                p: "We rotate cooler stock weekly. If a six-pack is anywhere close to its sell-by, it's discounted to move or gone. You will not find dusty IPAs at Liquor Studio.",
            },
            {
                h: "Cold to go",
                p: "Pretty much everything in the cooler is cold and ready to drink. Boat coolers, tailgates, last-minute cookouts — we've got you.",
            },
        ],
    },
    "missouri-liquor-laws": {
        kind: "guide",
        breadcrumb: [{ label: "Guides" }, { label: "Missouri Liquor Laws" }],
        title: "Missouri Liquor Laws — A Plain-English Guide",
        h1: ["Missouri liquor", "laws, explained."],
        eyebrow: "Guide",
        description:
            "Plain-English reference for Missouri liquor laws: legal age, sale hours, ID requirements, Sunday sales, package laws, and dram shop. Updated 2026.",
        keywords: "Missouri liquor laws, Missouri legal drinking age, Missouri Sunday liquor sales, Missouri package store laws, MO liquor ID law",
        hero: IMG.heroBottlesDark,
        intro:
            "Missouri's alcohol laws are friendlier than most. Here's what actually matters for buyers, sellers, and event hosts in Henry County and across the state. This is a plain-English summary, not legal advice — consult Missouri Statutes Chapter 311 or an attorney for specifics.",
        sections: [
            {
                h: "Legal age — 21",
                p: "You must be 21 to purchase or possess alcohol in Missouri. We card anyone who appears under 40. Valid IDs: driver's license, state ID, US passport, military ID. Out-of-state IDs accepted. No exceptions.",
            },
            {
                h: "Sale hours",
                p: "Liquor stores in Missouri may sell 6 AM – 1:30 AM Monday through Saturday, and 9 AM – Midnight on Sundays. Sunday morning sales (before 9 AM) are NOT permitted. We open at 10 AM seven days for consistency.",
            },
            {
                h: "ID requirements",
                p: "Missouri retailers must request ID from anyone who appears under 21. Per our policy and state best-practice, we card anyone who appears under 40. Refusing to provide a valid ID = no sale, every time. We are not allowed to accept expired IDs.",
            },
            {
                h: "Sunday & holiday rules",
                p: "Sunday sales legal 9 AM – Midnight. Christmas Day: liquor stores must close. Election Day: alcohol sales are NOT prohibited in Missouri (this is a common myth — most states ended the restriction by 2014; Missouri ended its in 2005).",
            },
            {
                h: "Package vs. on-premise",
                p: "Liquor Studio is a package retailer. We do not serve drinks on-premise. We do not pour samples. We may offer tastings only at properly licensed events.",
            },
            {
                h: "Shipping & out-of-state",
                p: "We do not ship outside Missouri. Direct-to-consumer wine shipping into Missouri is permitted from licensed out-of-state wineries (up to 2 cases/person/year). Spirits shipping rules vary — call the Missouri Alcohol & Tobacco Control division (573-751-2333) for specifics.",
            },
            {
                h: "Dram shop liability",
                p: "Missouri has a dram shop law (RSMo 537.053) — retailers can be liable for over-serving visibly intoxicated persons or selling to minors. We refuse sales to anyone visibly intoxicated, no matter how busy we are.",
            },
            {
                h: "Open container",
                p: "Missouri does NOT have a state-wide open container law for passengers in private vehicles — but most cities, including Clinton, do. Drink responsibly, never drink and drive.",
            },
        ],
    },

    // ============ Phase 6 Batch 2 — service/info pages ============

    "gift-cards": {
        kind: "service",
        breadcrumb: [{ label: "Gift Cards" }],
        title: "Liquor Gift Cards — Clinton, MO",
        h1: ["The right gift,", "every time."],
        eyebrow: "Gift Cards",
        description: "Liquor Studio gift cards — digital or physical — from $25 up. The thoughtful gift for the bourbon lover, wine drinker, or beer enthusiast on your list. Redeemable in-store at 309 S 2nd St, Clinton, MO.",
        keywords: "liquor gift card Clinton MO, bourbon gift card Missouri, wine gift card, liquor studio gift card",
        hero: IMG.bourbonCollectionShelf,
        intro: "When in doubt, gift the bourbon-shop gift card. Available in any denomination from $25 up, redeemable at our Clinton store or online at shop.liquor.studio. No expiration, no fees, no fine print — just a gift that lets the recipient pick exactly what they want.",
        sections: [
            { h: "Two ways to gift", p: "**Digital cards** are emailed to the recipient with a custom note — perfect for last-minute gifts, out-of-town family, and same-day birthdays. **Physical cards** are available at the counter in a Liquor Studio kraft envelope with hand-tied gold ribbon. Both formats use the same redemption code." },
            { h: "Any denomination", p: "Choose any amount from $25 to $1,000. Recipients can use part now and save the rest — balances roll over and don't expire. We can split a gift across a wine bottle, a six-pack, and bourbon, all on one card.", cta: { label: "Order online", to: "/contact" } },
            { h: "Perfect for", p: "Housewarmings, weddings, retirement parties, milestone birthdays, employee thank-yous, corporate client gifts, and the relative who already has everything. Pair a card with a curated basket and we'll wrap the whole thing." },
        ],
    },

    "curbside-pickup": {
        kind: "service",
        breadcrumb: [{ label: "Curbside Pickup" }],
        title: "Curbside Pickup — Liquor Studio Clinton MO",
        h1: ["Order ahead.", "Pull up. Done."],
        eyebrow: "Curbside Pickup",
        description: "Curbside pickup at Liquor Studio Clinton, MO. Order online or call (660) 383-1751 — we'll have your case waiting and bring it to your vehicle. ID verified at the curb.",
        keywords: "curbside pickup liquor Clinton MO, liquor curbside Missouri, liquor studio pickup",
        hero: IMG.storeInteriorWarm,
        intro: "Skip the aisle, keep the engine running. Order online at shop.liquor.studio or call us, pull into our south-lot when you arrive, and we'll bring your order to your driver-side window. Average curbside is under 90 seconds from arrival to load-up.",
        sections: [
            { h: "Step 1 — Order", p: "Browse and check out at **[shop.liquor.studio](https://shop.liquor.studio)** or call **(660) 383-1751**. Pay online or by phone. Tell us a pickup window. Most orders are ready within an hour during business hours." },
            { h: "Step 2 — Arrive", p: "Pull into the **south-side parking lot** at 309 S 2nd St. Plenty of room for trucks and trailers. Look for the gold-and-red Liquor Studio sign." },
            { h: "Step 3 — Show ID", p: "Per Missouri law, we **verify a valid government-issued ID at the curb** for every alcohol order. Have it ready and visible — no exceptions, no matter how busy or how cold." },
            { h: "Step 4 — Roll", p: "We load your order, hand you a receipt, and you're back on the road. Average curbside transaction: 90 seconds. Fastest route to your Truman Lake weekend." },
        ],
    },

    "events-and-weddings": {
        kind: "service",
        breadcrumb: [{ label: "Events & Weddings" }],
        title: "Wedding Bar & Event Liquor — Clinton, MO",
        h1: ["Wedding bars,", "without the panic."],
        eyebrow: "Events & Weddings",
        description: "Liquor Studio handles wedding bars, corporate events, and milestone parties in Clinton, MO. Bulk discounts, returns on unopened bottles, expert quantity planning. Call (660) 383-1751.",
        keywords: "wedding bar Clinton MO, wedding liquor Missouri, event liquor Clinton, corporate event bar Henry County",
        hero: IMG.wineBottlesElegant,
        intro: "Stocking a wedding bar should not be where you panic. We've planned bars for 50-guest backyard receptions and 400-guest country club weddings across Henry County and Truman Lake. Bring us the guest count, the bar style (full / beer-and-wine / signature cocktail), and the date — we handle the math.",
        sections: [
            { h: "Bar size — quick reference", p: "**Full bar for 100 guests, 4-hour reception:** ~6 cases beer, 4 cases wine (half white / half red), 2 bottles each of vodka/whiskey/rum/tequila/gin, plus mixers. **Beer-and-wine bar for 100 guests:** 8 cases beer, 6 cases wine. We scale these for your headcount and add a signature-cocktail quote if you want one." },
            { h: "Returns on unopened bottles", p: "Order more than you need without worry — **unopened, undamaged bottles can be returned for full credit within 7 days** of your event. Most weddings come back with 10–20% returns and we refund them, no questions, no restocking fee.", cta: { label: "Start a quote", to: "/contact" } },
            { h: "Case discounts + delivery", p: "Case pricing on 6+ bottles wine, 12+ bottles spirits, full kegs. Event-quantity delivery available throughout Henry County, Warsaw, Sedalia, and the Truman Lake region. Email **hello@liquor.studio** or call **(660) 383-1751** with your event date." },
            { h: "Plan ahead — but not too far", p: "We recommend **4 weeks ahead** for full-service wedding bar consultation, **1 week** for corporate events, **48 hours** for showers and smaller parties. We can usually accommodate last-minute pivots — just call." },
        ],
    },

    "business-accounts": {
        kind: "service",
        breadcrumb: [{ label: "Business Accounts" }],
        title: "Business & Corporate Liquor Accounts — Clinton, MO",
        h1: ["A dedicated", "buyer, on call."],
        eyebrow: "Business Accounts",
        description: "Net-30 business accounts for restaurants, venues, offices, and corporate gifting in Clinton, MO. Case pricing, dedicated rep, fast turnaround. Apply via Liquor Studio.",
        keywords: "wholesale liquor Clinton MO, business liquor account Missouri, corporate gifting liquor Clinton, restaurant supply liquor MO",
        hero: IMG.storeInteriorWarm,
        intro: "If your business buys spirits, beer, or wine — for a venue, an office gift program, employee recognition, holiday parties, or restaurant private dining — a Liquor Studio business account saves you time and money. One point of contact, case pricing, and net-30 terms.",
        sections: [
            { h: "Who qualifies", p: "Restaurants and licensed venues, event spaces, corporate offices with annual spend over $1,000, hotel concierge programs, and gift-program managers. Apply with your EIN, business license (if licensed), and the name of the person we should call. Approval typically within 2 business days." },
            { h: "What you get", p: "**Dedicated rep** who knows your inventory and preferences. **Case pricing** on 6+ wine / 12+ spirits / kegs. **Net-30 invoicing** for approved accounts. **Priority allocation** on hard-to-find bottles for corporate gifting. **Curbside or delivery** depending on order size and location.", cta: { label: "Apply", to: "/contact" } },
            { h: "How to start", p: "Email **hello@liquor.studio** with your business name, contact, and rough monthly spend, or call **(660) 383-1751**. We'll send a one-page application and get your account set up." },
        ],
    },

    // Truman Lake marina deep-dive (located under /local/, not /locations/)
    "truman-lake-marina-guide": {
        kind: "guide",
        breadcrumb: [{ label: "Local" }, { label: "Truman Lake Marina Guide" }],
        title: "Truman Lake Marina Guide — Liquor Studio",
        h1: ["The Truman Lake", "marina playbook."],
        eyebrow: "Local Guide",
        description: "Marina-by-marina guide to Truman Lake from Liquor Studio, Clinton MO. Long Shoal, Berry Bend, Sterett Creek, Bucksaw, Tightwad. Drive times, what to bring, lake-day picks.",
        keywords: "Truman Lake marina guide, Berry Bend marina, Sterett Creek, Bucksaw marina, Long Shoal, Tightwad marina, Truman Reservoir liquor",
        hero: IMG.trumanLakeSunset,
        intro: "Truman Reservoir spans 55,600 acres across Benton, Henry, Hickory, and St. Clair counties — Missouri's largest flood-control reservoir and one of the most underrated lakes in the Midwest. Five major marinas plus a dozen smaller access points. Clinton is the natural staging town: it's 10–35 minutes from any marina, and Liquor Studio is the last serious liquor stop before you hit the water.",
        sections: [
            { h: "Long Shoal Marina (Warsaw)", p: "On the east end of the lake near Warsaw. Full-service marina with fuel, slips, store, and ramp. **~25 minutes from Liquor Studio** via MO-7 E. Best for: Warsaw-side day trips, fishing the deeper main-channel water." },
            { h: "Berry Bend Marina", p: "South-central lake, easy access from MO-7. **~12 minutes from Liquor Studio.** Fuel, slips, ramp, and a popular swim beach. Best for: families, party coves, easy load-and-go weekends. Most boat traffic on summer Saturdays — get out early." },
            { h: "Sterett Creek Marina (Warsaw)", p: "Just south of Warsaw on Sterett Creek. **~14 minutes from Liquor Studio.** Slips, fuel, restaurant on-site. Best for: weekenders who want to dock-and-dine, sunset cruisers." },
            { h: "Bucksaw Marina", p: "Far east end, near Bucksaw State Park. **~30 minutes from Liquor Studio** via MO-7 then south. Largest marina on the lake — fuel, slips, ramp, restaurant, full store. Best for: full-weekend houseboat rentals, fishing tournaments, anyone who wants the marina-as-destination experience." },
            { h: "Talley Bend Marina", p: "South side near the dam. **~18 minutes from Liquor Studio.** Quieter than Berry Bend, popular with anglers. Ramp + fuel + small store. Best for: morning fishing trips, cove-camping weekends." },
            { h: "Tightwad area", p: "Public ramps near the village of Tightwad (population: 50, no joke). **~22 minutes from Liquor Studio.** No marina services — bring everything. Best for: kayakers, jon-boat anglers, anyone who wants empty water on a Saturday." },
            { h: "What to bring from Liquor Studio", p: "Lake-friendly priorities: **canned everything** (boats + glass don't mix), **single-serve wines** for pontoon docking, **hard seltzers** for hot afternoons, **a high-proof bourbon** for the post-sunset campfire, **ice** (when we have it), **NA beer** for the captain, **koozies** at the counter. Pack a cooler that closes — wind and waves spill open cups." },
            { h: "Stay safe on the water", p: "Missouri operates a 0.08% BAC limit for boat operators just like for drivers. Designate a sober captain, wear PFDs, and pace yourselves on hot days. The Henry County Sheriff and MO State Highway Patrol both run boat patrols on summer weekends. We'll never sell to someone clearly intoxicated — and we'd rather you call us for a rideshare than risk it." },
            { h: "Call ahead, load fast", p: "Trying to launch at 11 AM Saturday? Call us Friday at **(660) 383-1751** and we'll have your case bundled, iced (when available), and ready to load. Most Truman Lake regulars do this — saves 20 minutes you'd rather spend on the water.", cta: { label: "Browse spirits", to: "/spirits" } },
        ],
    },

};

export default function LandingPage() {
    const params = useParams();
    const location = useLocation();
    const reduce = useReducedMotion();
    // Derive slug: prefer route param, otherwise use last segment of pathname
    const slug = params.slug || location.pathname.split("/").filter(Boolean).pop();
    const data = CONTENT[slug];
    if (!data) return <Navigate to="/404" replace />;

    // Use the actual pathname for canonical URL — works for all variants
    const path = location.pathname;

    const businessLd = {
        "@context": "https://schema.org",
        "@type": ["LiquorStore", "LocalBusiness"],
        "@id": `${BIZ.siteUrl}/#business`,
        name: BIZ.name,
        url: `${BIZ.siteUrl}${path}`,
        image: data.hero,
        telephone: BIZ.phone,
        priceRange: "$$",
        address: {
            "@type": "PostalAddress",
            streetAddress: BIZ.address.line1,
            addressLocality: BIZ.address.city,
            addressRegion: BIZ.address.state,
            postalCode: BIZ.address.zip,
            addressCountry: BIZ.address.country,
        },
        geo: { "@type": "GeoCoordinates", latitude: 38.369, longitude: -93.782 },
        areaServed: BIZ.areasServed.map((a) => ({ "@type": "Place", name: a })),
    };
    const breadcrumbLd = buildBreadcrumbLd(data.breadcrumb);

    return (
        <>
            <SEO
                title={data.title}
                description={data.description}
                path={path}
                image={data.hero}
                keywords={data.keywords}
                jsonLd={[businessLd, breadcrumbLd]}
            />
            <Breadcrumbs items={data.breadcrumb} />

            {/* Hero */}
            <section
                style={{ position: "relative", paddingTop: 40, paddingBottom: 90, overflow: "hidden" }}
                data-testid="landing-hero"
            >
                <div
                    style={{
                        position: "absolute",
                        inset: 0,
                        backgroundImage: `linear-gradient(180deg, rgba(10,10,10,0.5) 0%, rgba(10,10,10,0.95) 100%), url("${data.hero}")`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        zIndex: 0,
                    }}
                    aria-hidden="true"
                />
                <div className="ls-container" style={{ position: "relative", zIndex: 1 }}>
                    <motion.div
                        initial={reduce ? false : { opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="max-w-3xl"
                    >
                        <span className="eyebrow">{data.eyebrow}</span>
                        <h1
                            className="font-display mt-5 text-white"
                            style={{
                                fontSize: "clamp(40px, 6.5vw, 76px)",
                                fontWeight: 500,
                                lineHeight: 1.04,
                                letterSpacing: "-0.02em",
                            }}
                            data-testid="landing-h1"
                        >
                            {data.h1[0]}<br />
                            <em style={{ fontStyle: "italic", color: "var(--ls-gold)", fontWeight: 400 }}>
                                {data.h1[1]}
                            </em>
                        </h1>
                        <p
                            className="mt-6"
                            style={{
                                color: "rgba(255,255,255,0.85)",
                                fontSize: 18,
                                lineHeight: 1.7,
                                maxWidth: 640,
                            }}
                        >
                            {data.intro}
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <Link to="/visit" className="btn-primary" style={{ fontSize: 13, padding: "12px 22px" }} data-testid="landing-cta-visit">
                                Visit Our Store <ArrowRight size={14} />
                            </Link>
                            <a href={BIZ.shopUrl} target="_blank" rel="noopener noreferrer"
                                className="btn-outline-gold" style={{ fontSize: 13, padding: "12px 22px" }} data-testid="landing-cta-shop">
                                Shop Online <ExternalLink size={13} />
                            </a>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Sections */}
            <section style={{ paddingBottom: 96 }} data-testid="landing-body">
                <div className="ls-container">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                        <div className="lg:col-span-8" style={{ display: "flex", flexDirection: "column", gap: 40 }}>
                            {data.sections.map((s, i) => (
                                <motion.article
                                    key={i}
                                    initial={reduce ? false : { opacity: 0, y: 16 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.45, delay: Math.min(i * 0.05, 0.3) }}
                                    data-testid={`landing-section-${i}`}
                                >
                                    <h2
                                        className="font-display text-white"
                                        style={{ fontSize: "clamp(26px, 3.5vw, 36px)", fontWeight: 500, lineHeight: 1.15, letterSpacing: "-0.015em" }}
                                    >
                                        {s.h}
                                    </h2>
                                    <p
                                        className="mt-4"
                                        style={{ color: "var(--ls-text-secondary)", fontSize: 16, lineHeight: 1.8 }}
                                    >
                                        {s.p}
                                    </p>
                                    {s.cta && (
                                        <Link to={s.cta.to} className="link-underline text-white mt-4 inline-flex items-center gap-1.5 text-sm">
                                            {s.cta.label} <ArrowRight size={13} />
                                        </Link>
                                    )}
                                </motion.article>
                            ))}
                        </div>

                        {/* Sidebar — quick facts */}
                        <aside className="lg:col-span-4">
                            <div
                                style={{
                                    background: "var(--ls-panel)",
                                    border: "1px solid var(--ls-border)",
                                    borderRadius: 8,
                                    padding: 28,
                                    position: "sticky",
                                    top: 100,
                                }}
                                data-testid="landing-sidecard"
                            >
                                <span className="eyebrow">Visit Liquor Studio</span>
                                <div className="mt-4 flex items-start gap-3">
                                    <MapPin size={16} style={{ color: "var(--ls-gold)", flexShrink: 0, marginTop: 2 }} />
                                    <div className="text-white" style={{ fontSize: 14, lineHeight: 1.6 }}>
                                        {BIZ.address.line1}<br />
                                        {BIZ.address.city}, {BIZ.address.state} {BIZ.address.zip}
                                    </div>
                                </div>
                                <div className="mt-4 flex items-start gap-3">
                                    <Clock size={16} style={{ color: "var(--ls-gold)", flexShrink: 0, marginTop: 2 }} />
                                    <div style={{ fontSize: 13, lineHeight: 1.7, color: "var(--ls-text-secondary)" }}>
                                        Mon – Thu · 10 AM – 10 PM<br />
                                        Fri – Sat · 10 AM – 11 PM<br />
                                        Sun · 10 AM – 10 PM
                                    </div>
                                </div>
                                <div className="mt-4 flex items-start gap-3">
                                    <Phone size={16} style={{ color: "var(--ls-gold)", flexShrink: 0, marginTop: 2 }} />
                                    <a href={`tel:${BIZ.phoneTel}`} className="link-underline text-white" style={{ fontSize: 14 }}>{BIZ.phone}</a>
                                </div>
                                <div className="mt-4 flex items-start gap-3">
                                    <Car size={16} style={{ color: "var(--ls-gold)", flexShrink: 0, marginTop: 2 }} />
                                    <div style={{ fontSize: 12, lineHeight: 1.6, color: "var(--ls-text-muted)" }}>
                                        Free street + lot parking. Two blocks south of the Henry County Square.
                                    </div>
                                </div>
                                <div className="mt-6 flex flex-col gap-2">
                                    <Link to="/visit" className="btn-primary" style={{ fontSize: 12, padding: "10px 16px", justifyContent: "center" }}>Get Directions</Link>
                                    <Link to="/contact" className="btn-ghost" style={{ fontSize: 12, padding: "10px 16px", justifyContent: "center" }}>Special Order</Link>
                                </div>
                            </div>
                        </aside>
                    </div>

                    {/* Related links footer */}
                    <div
                        style={{
                            marginTop: 80,
                            borderTop: "1px solid var(--ls-border)",
                            paddingTop: 40,
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                            gap: 22,
                        }}
                    >
                        <Link to="/spirits" className="link-underline text-white flex items-center gap-2" style={{ fontSize: 14 }}>
                            <Wine size={14} /> Full catalog
                        </Link>
                        <Link to="/reviews" className="link-underline text-white flex items-center gap-2" style={{ fontSize: 14 }}>
                            <Beer size={14} /> Customer reviews
                        </Link>
                        <Link to="/faq" className="link-underline text-white flex items-center gap-2" style={{ fontSize: 14 }}>
                            <ScrollText size={14} /> Common questions
                        </Link>
                        <Link to="/guides/missouri-liquor-laws" className="link-underline text-white flex items-center gap-2" style={{ fontSize: 14 }}>
                            <ScrollText size={14} /> Missouri liquor laws
                        </Link>
                    </div>
                </div>
            </section>
        </>
    );
}
